import React, { useRef } from 'react'
import { IconButton, Box, makeStyles } from '@material-ui/core'
import CancelIcon from '@material-ui/icons/Cancel'
import ClearIcon from '@material-ui/icons/Clear'
import MuiMenuItem from '@material-ui/core/MenuItem'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import PropTypes from 'proptypes'
import styled from 'styled-components'

const useStyles = makeStyles((theme) => ({
  searchIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 54,
    height: '100%',
    borderRadius: 0,
    borderLeft: `1px solid ${theme.colors.grey2} !important`,
    display: 'inline-flex',
    backgroundColor: theme.colors.white,
    '& svg': {
      margin: 'auto'
    }
  },
  clearIcon: {
    height: 48,
    margin: 'auto'
  }
}))
const BoxWrap = styled(Box)(({ theme, noicons }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minHeight: 54,
  borderBottom: noicons ? `1px solid ${theme.colors.grey2}` : 'none',
  [theme.breakpoints.up('sm')]: {
    borderBottom: 'none',
    borderLeft: noicons ? `1px solid ${theme.colors.grey2}` : 'none'
  },
  '& .rbt': {
    width: '100%',
    minHeight: 54,
    display: 'flex',
    alignItems: 'center'
  },
  '& .rbt-input-wrapper': {
    marginRight: 54
  },
  '& .rbt-input-multi, & .rbt-input': {
    width: '100%',
    '&.focus': {
      borderColor: 'transparent',
      boxShadow: 'none',
      outline: 'none'
    }
  },
  '& .rbt input': {
    width: '100%',
    borderColor: 'transparent',
    boxShadow: 'none',
    padding: `${theme.spacing(0, 2)} !important`,
    height: 34,
    fontSize: '1rem',
    '&::placeholder': {
      color: theme.colors.blue5
    }
  },
  '& .dropdown-menu': {
    zIndex: 3,
    boxShadow: theme.shadows[3]
  },
  '& .dropdown-item': {
    display: 'block',
    textAlign: 'left',
    textDecoration: 'none',
    color: theme.colors.blue5,
    backgroundColor: theme.colors.white,
    padding: 0,
    '&.active': {
      backgroundColor: theme.colors.cream
    },
    '&.disabled': {
      padding: theme.spacing(2)
    }
  }
}))
const SearchBadge = styled(Box)(({ theme }) => ({
  padding: '5px 7px',
  borderRadius: 20,
  backgroundColor: theme.colors.blue5,
  color: theme.colors.white,
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  margin: 2,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: '0.825rem'
}))
const BadgeIconButton = styled(IconButton)(({ theme }) => ({
  color: 'inherit',
  padding: 0,
  marginLeft: theme.spacing(1),
  '& svg': {
    fontSize: '1rem'
  }
}))
const ArrowIconButton = styled(IconButton)(({ theme }) => ({
  width: 'inherit',
  padding: theme.spacing(1)
}))
const FlagWrap = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 22,
  height: 22,
  overflow: 'hidden',
  borderRadius: '50%',
  marginRight: theme.spacing(1)
}))

const FlagWrapLarge = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  position: 'relative',
  width: 28,
  height: 28,
  overflow: 'hidden',
  borderRadius: '50%',
  flexShrink: 0,
  boxShadow: '0px 0px 2px 0px rgb(0 0 0 / 50%), 0px 0px 10px 0 rgb(0 0 0 / 10%)'
}))
const Flag = styled('img')(() => ({
  width: 'auto',
  height: '102%',
  display: 'block',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 'none'
}))
const SearchIcon = styled('img')(() => ({
  width: 25,
  height: 25,
  margin: 'auto'
}))
const InputWidthFlag = styled('input')(({ theme }) => ({
  width: '100%',
  outline: 'none',
  border: 'none',
  [theme.breakpoints.up('md')]: {
    height: '54px !important',
    fontSize: '1.5rem !important'
  }
}))
function SearchBox(props) {
  const ref = useRef()
  const classes = useStyles(props)
  const renderToken = (option, props, index) => {
    return (
      <SearchBadge
        display="flex"
        key={index}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <FlagWrap>
          <Flag src={`../../../images/flags/${option.iso.toLowerCase()}.svg`} alt={option.name} />
        </FlagWrap>
        {option.name}
        <BadgeIconButton
          onClick={(e) => {
            e.preventDefault()
            props.onRemove(option)
          }}
        >
          <ClearIcon />
        </BadgeIconButton>
      </SearchBadge>
    )
  }

  return (
    <BoxWrap noicons={props.noIcons ? 'true' : undefined}>
      {props.multiple ? (
        <Typeahead
          id="country-search"
          multiple
          placeholder={props.placeholder}
          labelKey="name"
          options={props.countries}
          selected={props.selectedCountries}
          onChange={props.setSelectedCountries}
          renderToken={props.multiple ? renderToken : ''}
          shouldSelect={true}
          positionFixed={true}
          paginate={false}
          maxResults={props.countries.length}
          ref={ref}
          emptyLabel={props.noMatchesMessage}
          renderMenu={(results, menuProps) => (
            <Menu {...menuProps}>
              {results.map((result, index) => (
                <MenuItem option={result} position={index} iso={result.iso} key={result.iso}>
                  <MuiMenuItem>{result.name}</MuiMenuItem>
                </MenuItem>
              ))}
            </Menu>
          )}
        />
      ) : (
        <Typeahead
          id="country-selector"
          placeholder={props.placeholder}
          labelKey="name"
          options={props.countries}
          selected={props.selectedCountries}
          onChange={props.setSelectedCountries}
          onFocus={() => {
            props.setPrevious()
            props.setSelectedCountries([])
          }}
          minLength={0}
          shouldSelect={false}
          positionFixed={true}
          paginate={false}
          maxResults={props.countries.length}
          ref={ref}
          emptyLabel={props.noMatchesMessage}
          renderInput={({ inputRef, referenceElementRef, ...inputProps }) => {
            let iso = ''
            if (props.selectedCountries && props.selectedCountries[0]) {
              iso = props.selectedCountries[0].iso
            }

            return (
              <Box display="flex" alignItems="center">
                {inputProps.value && iso && (
                  <FlagWrapLarge>
                    <Flag src={`../../../images/flags/${iso}.svg`} alt={inputProps.value} />
                  </FlagWrapLarge>
                )}
                <InputWidthFlag
                  fullWidth
                  {...inputProps}
                  ref={(input) => {
                    inputRef(input)
                    referenceElementRef(input)
                  }}
                />
              </Box>
            )
          }}
          renderMenu={(results, menuProps) => (
            <Menu {...menuProps}>
              {results.map((result, index) => (
                <MenuItem option={result} position={index} iso={result.iso} key={result.iso}>
                  <MuiMenuItem>{result.name}</MuiMenuItem>
                </MenuItem>
              ))}
            </Menu>
          )}
        />
      )}
      {props.noIcons ? (
        <Box className={classes.searchIcon}>
          <ArrowIconButton
            onClick={() => {
              props.setPrevious()
              ref.current.clear()
              props.setSelectedCountries([])
              ref.current.focus()
            }}
          >
            <KeyboardArrowDownIcon fontSize="large" />
          </ArrowIconButton>
        </Box>
      ) : props.selectedCountries && props.selectedCountries.length > 0 ? (
        <Box className={classes.searchIcon}>
          <IconButton
            onClick={() => {
              ref.current.clear()
              props.setSelectedCountries([])
            }}
            className={classes.clearIcon}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      ) : (
        <Box className={classes.searchIcon}>
          <SearchIcon src="../../../images/icons/search.svg" alt="search" />
        </Box>
      )}
    </BoxWrap>
  )
}

export default SearchBox

SearchBox.propTypes = {
  placeholder: PropTypes.string,
  noResultsText: PropTypes.string,
  selectedCountries: PropTypes.array,
  countries: PropTypes.array
}
